import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowLeft,
    ArrowUpRight,
    BookOpen,
    Check,
    Clock,
    Copy,
    Linkedin,
    Send,
    Share2,
    Sparkles,
    Twitter,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import {
    CAT_COLORS,
    POSTS,
    findPostBySlug as findStaticPost,
    relatedPosts as relatedStaticPosts,
    nextPostAfter as nextStaticPost,
} from "../data/blogPosts";
import {
    fetchBlogPost,
    fetchBlogPosts,
    findPostBySlug,
    relatedPosts,
    nextPostAfter,
} from "../lib/cmsApi";
import { usePageSeo } from "../hooks/usePageSeo";
import { SITE_NAME } from "../data/seoConfig";
import { WEBSITE_URL } from "../lib/env";

/* ──────────────────────────────────────────────
 *  Typed block renderer for post body
 * ────────────────────────────────────────────── */

const BodyBlock = ({ b, accent }) => {
    if (b.type === "lede")
        return (
            <p className="font-display text-xl sm:text-2xl text-zinc-100 leading-relaxed font-light first-letter:text-4xl first-letter:font-semibold first-letter:mr-1">
                {b.text}
            </p>
        );
    if (b.type === "p")
        return (
            <p className="text-zinc-300 text-base sm:text-[17px] leading-[1.8]">
                {b.text}
            </p>
        );
    if (b.type === "h2")
        return (
            <h2
                id={slugify(b.text)}
                className="scroll-mt-32 font-outfit text-2xl sm:text-3xl font-semibold text-white pt-8"
            >
                {b.text}
            </h2>
        );
    if (b.type === "callout")
        return (
            <aside
                className={`relative rounded-2xl border ${accent.chip} p-5 sm:p-6 my-3`}
            >
                <div
                    className={`font-code text-[10px] uppercase tracking-[0.2em] ${accent.accent} mb-2 inline-flex items-center gap-1.5`}
                >
                    <Sparkles className="w-3 h-3" />
                    {b.title || "// note"}
                </div>
                <p className="text-zinc-100 leading-relaxed">{b.text}</p>
            </aside>
        );
    if (b.type === "list")
        return (
            <ul className="space-y-2.5">
                {b.items.map((it) => (
                    <li
                        key={it}
                        className="flex items-start gap-3 text-zinc-300 leading-relaxed"
                    >
                        <span
                            className={`shrink-0 mt-2 w-1.5 h-1.5 rounded-full ${accent.accent.replace("text-", "bg-")}`}
                        />
                        <span>{it}</span>
                    </li>
                ))}
            </ul>
        );
    if (b.type === "code")
        return (
            <pre className="rounded-2xl border border-white/[0.08] bg-black/60 px-5 py-4 overflow-x-auto font-code text-[13px] leading-relaxed text-zinc-200 my-3">
                <div className="font-code text-[10px] uppercase tracking-[0.18em] text-zinc-600 mb-2">
                    // {b.lang || "bash"}
                </div>
                {b.code}
            </pre>
        );
    if (b.type === "quote")
        return (
            <blockquote className="relative pl-6 py-2 border-l-2 border-electric/60 my-2">
                <p className="font-display text-lg sm:text-xl text-white leading-snug italic">
                    “{b.text}”
                </p>
                {b.who && (
                    <footer className="mt-3 text-sm text-zinc-500 font-code">
                        — {b.who}
                    </footer>
                )}
            </blockquote>
        );
    return null;
};

const slugify = (s) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

/* ──────────────────────────────────────────────
 *  Page
 * ────────────────────────────────────────────── */

export default function BlogPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [allPosts, setAllPosts] = useState(POSTS);
    const [loading, setLoading] = useState(true);
    const [activeH2, setActiveH2] = useState(null);
    const [email, setEmail] = useState("");
    const articleRef = useRef(null);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchBlogPost(slug).catch(() => null),
            fetchBlogPosts().catch(() => null),
        ])
            .then(([single, listData]) => {
                const list = listData?.posts?.length ? listData.posts : POSTS;
                setAllPosts(list);
                setPost(single || findPostBySlug(list, slug) || findStaticPost(slug) || null);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    // Scroll-to-top when slug changes
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [slug]);

    usePageSeo(
        post
            ? {
                  title: `${post.title} — ${SITE_NAME} Blog`,
                  description: post.excerpt,
                  keywords: [post.tag, ...(post.tags || [])].filter(Boolean).join(", "),
                  canonical: `${WEBSITE_URL}/blog/${post.slug}`,
                  ogType: "article",
                  article: {
                      title: post.title,
                      description: post.excerpt,
                      datePublished: post.date,
                      author: post.author,
                  },
              }
            : null,
    );

    // Build TOC
    const toc = useMemo(() => {
        if (!post) return [];
        return post.body
            .filter((b) => b.type === "h2")
            .map((b) => ({ id: slugify(b.text), label: b.text }));
    }, [post]);

    // Scroll-spy
    useEffect(() => {
        if (!toc.length) return;
        const obs = new IntersectionObserver(
            (entries) => {
                const top = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (top) setActiveH2(top.target.id);
            },
            { rootMargin: "-30% 0px -55% 0px", threshold: 0 },
        );
        toc.forEach((t) => {
            const el = document.getElementById(t.id);
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, [toc]);

    if (loading) {
        return (
            <div className="pt-32 max-w-3xl mx-auto px-6 sm:px-8 text-center text-zinc-500">
                Loading…
            </div>
        );
    }

    if (!post) {
        return (
            <div className="pt-32 max-w-3xl mx-auto px-6 sm:px-8 text-center">
                <p className="font-code text-xs uppercase tracking-[0.25em] text-zinc-500 mb-3">
                    // 404 · post not found
                </p>
                <h1 className="font-display text-3xl text-white">
                    We couldn&apos;t find that story.
                </h1>
                <Link
                    to="/blog"
                    className="inline-flex items-center gap-2 mt-6 text-electric hover:text-white transition"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to the blog
                </Link>
            </div>
        );
    }

    const accent = CAT_COLORS[post.category];
    const related = relatedPosts(allPosts, post, 3).length
        ? relatedPosts(allPosts, post, 3)
        : relatedStaticPosts(post.slug, 3);
    const next = nextPostAfter(allPosts, post) || nextStaticPost(post.slug);

    const share = (target) => {
        const url = `https://apexbrain.in/blog/${post.slug}`;
        if (target === "copy") {
            navigator.clipboard.writeText(url);
            toast.success("Link copied to clipboard.");
            return;
        }
        const t = encodeURIComponent(post.title);
        const u = encodeURIComponent(url);
        const map = {
            twitter: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
            linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
        };
        window.open(map[target], "_blank", "noopener");
    };

    const subscribe = (e) => {
        e.preventDefault();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            toast.error("That email doesn’t look right.");
            return;
        }
        toast.success("You're in. First issue lands Friday.");
        setEmail("");
    };

    return (
        <div className="pt-24 lg:pt-28" data-testid="blog-post-page">
            <Toaster position="top-right" theme="dark" richColors />

            {/* Hero */}
            <section className="relative overflow-hidden py-12 lg:py-16">
                <div className="absolute inset-0 radial-glow pointer-events-none" />
                <div className="relative max-w-5xl mx-auto px-6 sm:px-8">
                    <Link
                        to="/blog"
                        className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-white transition mb-8 font-code"
                        data-testid="blog-post-back"
                    >
                        <ArrowLeft className="w-3 h-3" />
                        back to the blog
                    </Link>

                    <div className="flex flex-wrap items-center gap-3 mb-6">
                        <span
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 ${accent.chip}`}
                        >
                            <span
                                className={`text-[10px] font-code uppercase tracking-[0.2em] font-semibold ${accent.accent}`}
                            >
                                {post.tag}
                            </span>
                        </span>
                        <span className="font-code text-[11px] text-zinc-500">
                            {post.date}
                        </span>
                        <span className="font-code text-[11px] text-zinc-500 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {post.read}
                        </span>
                    </div>

                    <motion.h1
                        initial={{ opacity: 0, y: 18, filter: "blur(8px)" }}
                        animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                        className="font-display text-3xl sm:text-5xl lg:text-[3.5rem] font-semibold text-white leading-[1.05] tracking-tight"
                        data-testid="blog-post-title"
                    >
                        {post.title}
                    </motion.h1>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15 }}
                        className="mt-6 text-zinc-400 text-lg leading-relaxed font-light max-w-3xl"
                    >
                        {post.excerpt}
                    </motion.p>

                    <div className="mt-9 flex flex-wrap items-center justify-between gap-4">
                        <AuthorBlock post={post} />
                        <ShareRow share={share} />
                    </div>
                </div>
            </section>

            {/* Cover art band */}
            <section className="relative">
                <div className="max-w-6xl mx-auto px-6 sm:px-8">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="relative aspect-[2.6/1] rounded-3xl overflow-hidden border border-white/[0.08] bg-obsidian-200/60"
                        data-testid="blog-post-cover"
                    >
                        <div
                            className={`absolute inset-0 bg-gradient-to-br ${accent.ring} opacity-90`}
                        />
                        <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
                        <CoverDecor accent={accent.accent} />
                    </motion.div>
                </div>
            </section>

            {/* Body + sidebar */}
            <section className="relative py-16">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
                    {/* TOC */}
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-28">
                            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-zinc-600 mb-3">
                                // contents
                            </p>
                            <ul
                                className="space-y-1.5"
                                data-testid="blog-post-toc"
                            >
                                {toc.map((t) => {
                                    const isActive = t.id === activeH2;
                                    return (
                                        <li key={t.id}>
                                            <a
                                                href={`#${t.id}`}
                                                className={`block text-sm leading-snug transition ${
                                                    isActive
                                                        ? "text-white font-semibold"
                                                        : "text-zinc-500 hover:text-zinc-200"
                                                }`}
                                            >
                                                <span
                                                    className={`inline-block w-1 h-1 mr-2 rounded-full align-middle ${isActive ? "bg-electric animate-pulse-glow" : "bg-zinc-700"}`}
                                                />
                                                {t.label}
                                            </a>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="mt-10 rounded-2xl border border-white/[0.06] bg-obsidian-200/60 p-4">
                                <p className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">
                                    // share
                                </p>
                                <ShareRow share={share} vertical />
                            </div>
                        </div>
                    </aside>

                    {/* Article body */}
                    <article
                        ref={articleRef}
                        className="lg:col-span-9 max-w-3xl space-y-5"
                        data-testid="blog-post-body"
                    >
                        {post.body.map((b, i) => (
                            <BodyBlock key={i} b={b} accent={accent} />
                        ))}

                        {/* Inline author bio */}
                        <div
                            className="mt-12 rounded-2xl border border-white/[0.08] bg-obsidian-200/70 p-6 flex items-start gap-4"
                            data-testid="blog-post-author"
                        >
                            <div
                                className={`shrink-0 inline-flex items-center justify-center rounded-full bg-gradient-to-br ${post.avatarBg} font-outfit font-bold text-obsidian-300 w-12 h-12`}
                            >
                                {post.avatar}
                            </div>
                            <div>
                                <div className="font-outfit text-base font-semibold text-white">
                                    {post.author}
                                </div>
                                <div className="text-[11px] text-zinc-500 font-code mb-2">
                                    {post.role}
                                </div>
                                <p className="text-sm text-zinc-400 leading-relaxed font-light">
                                    {post.bio}
                                </p>
                            </div>
                        </div>

                        {/* Tags */}
                        <div className="mt-8 flex flex-wrap items-center gap-2">
                            <span className="font-code text-[10px] uppercase tracking-[0.2em] text-zinc-600 mr-1">
                                // tags
                            </span>
                            {post.tags.map((t) => (
                                <span
                                    key={t}
                                    className="inline-flex items-center rounded-full border border-white/[0.08] bg-white/[0.02] px-2.5 py-0.5 text-[11px] text-zinc-300 font-code"
                                >
                                    #{t}
                                </span>
                            ))}
                        </div>
                    </article>
                </div>
            </section>

            {/* Newsletter inline */}
            <section className="relative py-8">
                <div className="max-w-4xl mx-auto px-6 sm:px-8">
                    <div className="relative rounded-3xl border border-electric/25 bg-gradient-to-br from-obsidian-100 to-obsidian-300 p-8 sm:p-10 overflow-hidden">
                        <div className="absolute -top-20 -right-20 w-64 h-64 bg-electric/20 blur-3xl rounded-full pointer-events-none" />
                        <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
                            <div className="md:col-span-7">
                                <p className="font-code text-[10px] uppercase tracking-[0.25em] text-amber-glow mb-2">
                                    // weekly · zero fluff
                                </p>
                                <h3 className="font-outfit text-xl sm:text-2xl text-white font-semibold">
                                    Get posts like this in your inbox.
                                </h3>
                                <p className="text-sm text-zinc-400 mt-1">
                                    14,200+ operators read along.
                                </p>
                            </div>
                            <form
                                onSubmit={subscribe}
                                className="md:col-span-5 flex gap-2"
                                data-testid="blog-post-newsletter"
                            >
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="work@yourcompany.com"
                                    className="flex-1 rounded-full bg-white/[0.04] border border-white/[0.08] focus:border-electric/50 outline-none text-sm text-white px-4 py-2.5 placeholder:text-zinc-600"
                                    data-testid="blog-post-newsletter-email"
                                />
                                <button
                                    type="submit"
                                    data-testid="blog-post-newsletter-submit"
                                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-gradient-to-b from-electric to-electric-deep btn-glow px-5 py-2.5 rounded-full hover:brightness-110 transition"
                                >
                                    Subscribe
                                    <Send className="w-3.5 h-3.5" />
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Related posts */}
            {related.length > 0 && (
                <section className="relative py-12" data-testid="blog-post-related">
                    <div className="max-w-7xl mx-auto px-6 sm:px-8">
                        <div className="flex items-end justify-between mb-6">
                            <h2 className="font-outfit text-xl sm:text-2xl text-white font-semibold">
                                Keep reading
                            </h2>
                            <Link
                                to="/blog"
                                className="text-xs text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
                            >
                                All posts <ArrowUpRight className="w-3 h-3" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {related.map((r) => {
                                const ac = CAT_COLORS[r.category];
                                return (
                                    <Link
                                        to={`/blog/${r.slug}`}
                                        key={r.id}
                                        className="group rounded-2xl border border-white/[0.06] bg-obsidian-200/70 overflow-hidden hover:border-white/15 transition-colors"
                                        data-testid={`blog-post-related-${r.id}`}
                                    >
                                        <div
                                            className={`relative h-28 bg-gradient-to-br ${ac.ring} opacity-90 overflow-hidden`}
                                        >
                                            <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
                                            <CoverDecor accent={ac.accent} />
                                            <span
                                                className={`absolute top-3 left-3 inline-flex items-center rounded-full border px-2 py-0.5 ${ac.chip}`}
                                            >
                                                <span
                                                    className={`text-[9px] font-code uppercase tracking-[0.18em] font-semibold ${ac.accent}`}
                                                >
                                                    {r.tag}
                                                </span>
                                            </span>
                                        </div>
                                        <div className="p-5">
                                            <h3 className="font-outfit text-base text-white font-semibold leading-snug group-hover:text-cyan-200 transition-colors line-clamp-2">
                                                {r.title}
                                            </h3>
                                            <p className="mt-2 text-xs text-zinc-500 line-clamp-2 leading-relaxed">
                                                {r.excerpt}
                                            </p>
                                            <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-code text-zinc-500">
                                                <Clock className="w-2.5 h-2.5" />
                                                {r.read}
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </div>
                </section>
            )}

            {/* Next post banner */}
            {next && (
                <section className="relative pb-28 pt-4">
                    <div className="max-w-5xl mx-auto px-6 sm:px-8">
                        <Link
                            to={`/blog/${next.slug}`}
                            className="group relative block rounded-3xl border border-white/10 bg-gradient-to-br from-obsidian-100 via-obsidian-200 to-obsidian-300 p-8 sm:p-10 overflow-hidden hover:border-electric/40 transition"
                            data-testid="blog-post-next"
                        >
                            <div className="absolute -top-20 -right-16 w-72 h-44 bg-electric/15 blur-[100px] rounded-full pointer-events-none" />
                            <p className="font-code text-[10px] uppercase tracking-[0.25em] text-amber-glow mb-3">
                                // next up
                            </p>
                            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                                <div>
                                    <h3 className="font-outfit text-2xl sm:text-3xl text-white font-semibold leading-snug max-w-2xl group-hover:text-cyan-200 transition-colors">
                                        {next.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-zinc-500 font-code inline-flex items-center gap-2">
                                        <BookOpen className="w-3 h-3" />
                                        {next.read} read
                                    </p>
                                </div>
                                <ArrowUpRight className="w-6 h-6 text-electric transition-transform group-hover:rotate-45" />
                            </div>
                        </Link>
                    </div>
                </section>
            )}
        </div>
    );
}

/* ──────────────────────────────────────────────
 *  helpers
 * ────────────────────────────────────────────── */

const AuthorBlock = ({ post }) => (
    <div className="flex items-center gap-3">
        <div
            className={`inline-flex items-center justify-center rounded-full bg-gradient-to-br ${post.avatarBg} font-outfit font-bold text-obsidian-300 w-10 h-10 text-sm`}
        >
            {post.avatar}
        </div>
        <div className="leading-tight">
            <div className="text-sm font-semibold text-white">
                {post.author}
            </div>
            <div className="text-[11px] text-zinc-500 font-code">
                {post.role}
            </div>
        </div>
    </div>
);

const ShareRow = ({ share, vertical }) => (
    <div
        className={`flex ${vertical ? "flex-col" : "flex-row"} items-center gap-2`}
    >
        <button
            onClick={() => share("twitter")}
            data-testid="blog-share-twitter"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] text-xs text-zinc-300 hover:text-white px-3 py-1.5 transition"
            aria-label="Share on Twitter"
        >
            <Twitter className="w-3 h-3" />
            <span>Tweet</span>
        </button>
        <button
            onClick={() => share("linkedin")}
            data-testid="blog-share-linkedin"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] text-xs text-zinc-300 hover:text-white px-3 py-1.5 transition"
            aria-label="Share on LinkedIn"
        >
            <Linkedin className="w-3 h-3" />
            <span>LinkedIn</span>
        </button>
        <button
            onClick={() => share("copy")}
            data-testid="blog-share-copy"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.06] text-xs text-zinc-300 hover:text-white px-3 py-1.5 transition"
            aria-label="Copy link"
        >
            <Copy className="w-3 h-3" />
            <span>Copy</span>
        </button>
    </div>
);

const CoverDecor = ({ accent }) => (
    <svg
        viewBox="0 0 600 300"
        preserveAspectRatio="none"
        aria-hidden
        className="absolute inset-0 w-full h-full pointer-events-none mix-blend-screen"
    >
        <defs>
            <linearGradient id="bg1" x1="0" x2="1" y1="0" y2="1">
                <stop offset="0%" stopColor="currentColor" stopOpacity="0.35" />
                <stop offset="100%" stopColor="currentColor" stopOpacity="0" />
            </linearGradient>
        </defs>
        <g className={accent}>
            <circle cx="500" cy="60" r="120" fill="url(#bg1)" />
            <circle cx="80" cy="240" r="80" fill="url(#bg1)" opacity="0.6" />
            <path
                d="M0,220 C150,180 320,260 600,200"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.4"
                strokeWidth="1"
            />
            <path
                d="M0,180 C200,140 350,220 600,160"
                fill="none"
                stroke="currentColor"
                strokeOpacity="0.25"
                strokeWidth="1"
            />
            <g
                stroke="currentColor"
                strokeOpacity="0.35"
                strokeWidth="1"
                fill="none"
            >
                <circle cx="500" cy="60" r="40" />
                <circle cx="500" cy="60" r="70" />
            </g>
            <g fill="currentColor" fillOpacity="0.65">
                <circle cx="500" cy="60" r="3" />
                <circle cx="80" cy="240" r="2.5" />
                <circle cx="220" cy="120" r="2" />
                <circle cx="380" cy="200" r="2" />
            </g>
        </g>
    </svg>
);
